import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

type Tier = "sovereign" | "boardroom" | "academy" | null;

interface AuthContextType {
  user: User | null;
  tier: Tier;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({ user: null, tier: null, loading: true, signOut: async () => {} });

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tier, setTier] = useState<Tier>(null);
  const [loading, setLoading] = useState(true);

  const fetchTier = async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("tier")
      .eq("user_id", userId)
      .limit(1)
      .single();
    setTier((data?.tier as Tier) || "academy");
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchTier(session.user.id);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchTier(session.user.id);
      else setTier(null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setTier(null);
  };

  return (
    <AuthContext.Provider value={{ user, tier, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
